/**
 * QuickBooks — push one EWI- invoice (S10, fieldiq-api POST
 * /earthworks/invoices/:id/qbo-push). Drawn for a SENT or PAID invoice; a
 * draft has not gone to the customer and a void one is final. The API refuses
 * the same cases and its refusal is what decides.
 *
 * WHAT QUICKBOOKS GETS is every line of this invoice, signed — the work, the
 * holdback withheld and the deposit credited — so QuickBooks' total is the
 * total on this screen. Nothing here computes it: the API returns its total and
 * QuickBooks' side by side, and a difference is SHOWN, not hidden.
 *
 * Refusals reuse the casing invoice's vocabulary and notices (QboPushNotices),
 * so the two screens cannot drift on copy or colour. Every mapping refusal
 * routes to the mapping screen's Earthworks tab, where the ew: items, tax
 * codes and customers live — not to the casing tab the refusal's kind names.
 *
 * Admin / office staff push; everyone else reads the badge.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import { usePushEwInvoiceToQbo } from '../../../hooks/useEwInvoices';
import { ewInstant, fmtMoney } from '../../../lib/earthworks';
import {
  QBO_FAULT_CTA,
  QBO_FAULT_MESSAGE,
  QBO_MAPPING_ROUTE,
  QBO_RECONNECT_ROUTE,
  qboConnectionFault,
  qboPushSuccessMessage,
  readQboErrorMessage,
  readQboPushRefusal,
  type QboConnectionFault,
  type QboPushRefusal,
} from '../../../lib/qbo-errors';
import { QboPushRefusalNotice } from '../../../components/invoices/QboPushNotices';
import type { EwInvoice, EwQboPushResult } from '../../../types/ew-invoices';
import { EW_PRIMARY, EwPanel } from '../EwBits';

export const EW_QBO_MAPPING_HREF = `${QBO_MAPPING_ROUTE}?kind=earthworks`;

/** Sent or paid: the invoices QuickBooks may hold. */
export const ewInvoiceShowsQbo = (status: string | null | undefined) => status === 'sent' || status === 'paid';

/** The earthworks route answers "never connected" as 409 + code; casing's as a 500 sentence. Both are the same fault. */
function faultOf(err: unknown): QboConnectionFault | null {
  const r = (err as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
  if (r?.status === 409 && r.data?.code === 'QBO_NOT_CONNECTED') return 'not_connected';
  return qboConnectionFault(err);
}

export function EwInvoiceQboPanel({ inv, canWrite, tz }: { inv: EwInvoice; canWrite: boolean; tz: string | null }) {
  const navigate = useNavigate();
  const push = usePushEwInvoiceToQbo();
  const [refusal, setRefusal] = useState<QboPushRefusal | null>(null);
  const [fault, setFault] = useState<QboConnectionFault | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EwQboPushResult | null>(null);
  const qboId = inv.qboInvoiceId ?? result?.qboInvoiceId ?? null;
  const cur = inv.currency;

  const run = () => {
    setRefusal(null); setFault(null); setError(null);
    push.mutate(inv.id, {
      onSuccess: (r) => setResult(r),
      onError: (err) => {
        const f = faultOf(err);
        if (f) { setFault(f); return; }
        const ref = readQboPushRefusal(err);
        if (ref) { setRefusal(ref); return; }
        setError(readQboErrorMessage(err) ?? 'The push to QuickBooks failed. Try again.');
      },
    });
  };

  return (
    <EwPanel eyebrow="Accounting" title="QuickBooks" testId="ew-invoice-qbo">
      <div className="space-y-3 px-4 py-4">
        {qboId ? (
          <p data-testid="ew-invoice-qbo-badge" className="text-sm text-fieldiq-text">
            <span className="inline-flex items-center gap-1 rounded-fi-panel bg-fi-signal-green px-2.5 py-1 font-semibold text-fi-signal-ink">
              ✓ In QuickBooks #{qboId}
            </span>
            <span className="ml-3 text-fieldiq-muted">
              {inv.qboSyncedAt || result?.syncedAt ? `Pushed ${ewInstant(inv.qboSyncedAt ?? result?.syncedAt ?? null, tz)}` : ''}
            </span>
          </p>
        ) : canWrite ? (
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className={EW_PRIMARY} onClick={run} disabled={push.isPending} data-testid="ew-invoice-qbo-push">
              {push.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ExternalLink className="size-4" aria-hidden="true" />}
              Push to QuickBooks
            </button>
            <span className="text-xs text-fieldiq-muted">
              Every line goes as it stands here — work, holdback and deposit — so QuickBooks' total is {fmtMoney(inv.total, cur)}.
            </span>
          </div>
        ) : (
          <p className="text-sm text-fieldiq-muted" data-testid="ew-invoice-qbo-none">Not in QuickBooks.</p>
        )}

        {result && (
          <p role="status" data-testid="ew-invoice-qbo-result" className="text-sm text-fieldiq-text">{qboPushSuccessMessage(result)}.</p>
        )}
        {result && !result.totalMatches && (
          <div role="alert" data-testid="ew-invoice-qbo-total-mismatch" data-tone="warning"
            className="flex items-start gap-3 rounded-fi-panel border border-fieldiq-accent/40 bg-fieldiq-accent/10 px-4 py-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-fieldiq-accent" aria-hidden="true" />
            <p className="text-sm text-fieldiq-text">
              QuickBooks holds this invoice at {fmtMoney(result.qboTotal, cur)}; FieldIQ's total is {fmtMoney(result.total, cur)}.
              Open it in QuickBooks and check its tax before you send it from there.
            </p>
          </div>
        )}
        {refusal && (
          <QboPushRefusalNotice refusal={refusal} onOpenMapping={() => navigate(EW_QBO_MAPPING_HREF)} onDismiss={() => setRefusal(null)} />
        )}
        {fault && (
          <div role="alert" data-testid="ew-invoice-qbo-fault" className="flex flex-wrap items-center gap-3 rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
            <span className="flex-1">{QBO_FAULT_MESSAGE[fault]}</span>
            <button type="button" className="fi-action fi-action--compact fi-action--primary" onClick={() => navigate(QBO_RECONNECT_ROUTE)}>
              {QBO_FAULT_CTA[fault]}
            </button>
          </div>
        )}
        {error && (
          <p role="alert" data-testid="ew-invoice-qbo-error" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">{error}</p>
        )}
      </div>
    </EwPanel>
  );
}
